import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'

export interface AdminOrganisation {
  id: string
  name: string
  slug: string
  description?: string
  website?: string
  industry?: string
  created_at: string
  updated_at: string
}

export interface CreateOrganisationData {
  name: string
  description?: string
  website?: string
  industry?: string
}

export interface UpdateOrganisationData {
  name?: string
  description?: string
  website?: string
  industry?: string
}

export interface OrganisationsQueryParams {
  limit?: number
}

export interface GenerateSamplesData {
  count: number
  industry_hints?: string[]
  company_hints?: string[]
}

export function useOrganisations(params?: OrganisationsQueryParams) {
  return useQuery({
    queryKey: ['admin-organisations', params],
    queryFn: async (): Promise<AdminOrganisation[]> => {
      const searchParams = new URLSearchParams()
      if (params?.limit) searchParams.append('limit', params.limit.toString())

      const url = `/api/organisations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch organisations')
      }

      const data = await response.json()
      return data || []
    },
    staleTime: 30 * 1000,
  })
}

export function useOrganisation(organisationId: string) {
  return useQuery({
    queryKey: ['admin-organisation', organisationId],
    queryFn: async (): Promise<AdminOrganisation> => {
      const response = await authorizedFetch(`/api/organisations/${organisationId}`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Organisation not found')
        }
        throw new Error('Failed to fetch organisation')
      }

      const data = await response.json()
      return data as AdminOrganisation
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
  })
}

export function useCreateOrganisation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateOrganisationData): Promise<AdminOrganisation> => {
      const response = await authorizedFetch('/api/organisations', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create organisation')
      }

      const result = await response.json()
      queryClient.invalidateQueries({ queryKey: ['admin-organisations'] })
      return result as AdminOrganisation
    },
  })
}

export function useUpdateOrganisation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ organisationId, data }: { organisationId: string; data: UpdateOrganisationData }): Promise<AdminOrganisation> => {
      const response = await authorizedFetch(`/api/organisations/${organisationId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update organisation')
      }

      const result = await response.json()
      queryClient.invalidateQueries({ queryKey: ['admin-organisations'] })
      queryClient.invalidateQueries({ queryKey: ['admin-organisation', organisationId] })
      return result as AdminOrganisation
    },
  })
}

export function useDeleteOrganisation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organisationId: string): Promise<void> => {
      const response = await authorizedFetch(`/api/organisations/${organisationId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete organisation')
      }

      queryClient.invalidateQueries({ queryKey: ['admin-organisations'] })
    },
  })
}

export function useGenerateOrganisationSamples() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: GenerateSamplesData): Promise<void> => {
      const response = await authorizedFetch('/api/organisations/generate-samples', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to generate sample organisations')
      }

      queryClient.invalidateQueries({ queryKey: ['admin-organisations'] })
    },
  })
}

